import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as mentorApi from '../api/mentorApi';
import * as mentorRequestLog from '../api/mentorRequestLog';

export default function ApiActivityLog() {
  const [entries, setEntries] = useState(() => mentorRequestLog.getEntries());
  const [serverEntries, setServerEntries] = useState([]);
  const [serverLoading, setServerLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [hostFilter, setHostFilter] = useState('all');
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);
  const [pathSearch, setPathSearch] = useState('');
  const [source, setSource] = useState('local');

  const refreshLocal = useCallback(() => {
    setEntries(mentorRequestLog.getEntries({
      host: hostFilter === 'all' ? null : hostFilter,
      errorsOnly,
      duplicatesOnly,
      pathSearch,
    }));
  }, [hostFilter, errorsOnly, duplicatesOnly, pathSearch]);

  useEffect(() => {
    return mentorRequestLog.subscribe(() => refreshLocal());
  }, [refreshLocal]);

  useEffect(() => {
    refreshLocal();
  }, [refreshLocal]);

  const loadServer = useCallback(async () => {
    setServerLoading(true);
    try {
      const rows = await mentorApi.getPaApiActivityLogs({
        host: hostFilter === 'all' ? undefined : hostFilter,
        path: pathSearch || undefined,
        errors_only: errorsOnly || undefined,
      });
      setServerEntries(rows);
    } catch (err) {
      console.warn('Server activity logs unavailable:', err);
      setServerEntries([]);
    } finally {
      setServerLoading(false);
    }
  }, [hostFilter, pathSearch, errorsOnly]);

  useEffect(() => {
    if (source === 'server') loadServer();
  }, [source, loadServer]);

  const displayEntries = source === 'server' ? serverEntries : entries;
  const summary = useMemo(() => mentorRequestLog.summarize(
    source === 'server' ? serverEntries.map(normalizeServerRow) : entries
  ), [source, serverEntries, entries]);

  const selected = displayEntries.find((r) => String(r.id) === String(selectedId))
    || displayEntries.find((r) => String(r.client_id) === String(selectedId));

  const contextWindow = useMemo(() => {
    if (!selected) return [];
    const seq = selected.sequence;
    const list = source === 'server' ? serverEntries : entries;
    return list
      .filter((r) => Math.abs((r.sequence || 0) - seq) <= 5)
      .sort((a, b) => a.sequence - b.sequence);
  }, [selected, source, serverEntries, entries]);

  const handleClear = async () => {
    await mentorRequestLog.clearLocal();
    refreshLocal();
  };

  const handleFlush = async () => {
    await mentorRequestLog.flushToServer();
    if (source === 'server') await loadServer();
  };

  const download = (content, filename, mime) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="animate-fade-in">
      <div className="dashboard-title-row">
        <div>
          <h1 className="dashboard-title">API Activity Log</h1>
          <p className="dashboard-subtitle">
            Exhaustive trail of mentor panel HTTP calls — Ritika and mentor-api hosts, caller tags, dedupe and cache hits.
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
        {[
          ['total', summary.total],
          ['network', summary.network],
          ['failures', summary.failures],
          ['429s', summary.rateLimited],
          ['deduped', summary.deduped],
          ['cache hits', summary.cacheHits],
          ['duplicates', summary.duplicates],
        ].map(([label, value]) => (
          <div key={label} className="year2-metric-chip" style={{ minWidth: '100px' }}>
            <div className="year2-metric-value">{value}</div>
            <div className="year2-metric-label">{label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
        <select className="form-control-select" value={source} onChange={(e) => setSource(e.target.value)} style={{ maxWidth: '160px' }}>
          <option value="local">This session</option>
          <option value="server">Server history</option>
        </select>
        <select className="form-control-select" value={hostFilter} onChange={(e) => setHostFilter(e.target.value)} style={{ maxWidth: '220px' }}>
          <option value="all">All hosts</option>
          <option value="api.ifspd.eu">api.ifspd.eu</option>
          <option value="mentor-api.portahead.com">mentor-api.portahead.com</option>
        </select>
        <input
          className="wizard-search-input"
          placeholder="Search path or caller tag…"
          value={pathSearch}
          onChange={(e) => setPathSearch(e.target.value)}
          style={{ maxWidth: '280px' }}
        />
        <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input type="checkbox" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} />
          Errors only
        </label>
        <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input type="checkbox" checked={duplicatesOnly} onChange={(e) => setDuplicatesOnly(e.target.checked)} />
          Duplicates only
        </label>
        <button type="button" className="btn-form-toggle-outlined" onClick={refreshLocal}>Refresh</button>
        <button type="button" className="btn-form-toggle-outlined" onClick={handleFlush}>Flush to server</button>
        <button type="button" className="btn-form-toggle-outlined" onClick={handleClear}>Clear local</button>
        <button type="button" className="btn-form-toggle-outlined" onClick={() => download(mentorRequestLog.exportJson(), 'mentor-api-log.json', 'application/json')}>Export JSON</button>
        <button type="button" className="btn-form-toggle-outlined" onClick={() => download(mentorRequestLog.exportCsv(), 'mentor-api-log.csv', 'text/csv')}>Export CSV</button>
      </div>

      {serverLoading && source === 'server' && (
        <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading server history…</p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '16px', alignItems: 'start' }}>
        <div className="placeholder-card" style={{ padding: 0, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                <th style={{ padding: '8px' }}>#</th>
                <th style={{ padding: '8px' }}>Time</th>
                <th style={{ padding: '8px' }}>Host</th>
                <th style={{ padding: '8px' }}>Method</th>
                <th style={{ padding: '8px' }}>Path</th>
                <th style={{ padding: '8px' }}>St</th>
                <th style={{ padding: '8px' }}>ms</th>
                <th style={{ padding: '8px' }}>Caller</th>
                <th style={{ padding: '8px' }}>Flags</th>
              </tr>
            </thead>
            <tbody>
              {displayEntries.map((row) => {
                const isDup = source === 'local' && mentorRequestLog.isDuplicateEntry(row, entries);
                const flags = [
                  row.deduped || row.outcome === 'deduped' ? 'DEDUP' : null,
                  row.cacheHit || row.outcome === 'cache_hit' ? 'CACHE' : null,
                  row.retried ? 'RETRY' : null,
                  row.rateLimited || row.rate_limited ? '429' : null,
                ].filter(Boolean).join(' ');
                return (
                  <tr
                    key={row.id || row.client_id}
                    onClick={() => setSelectedId(row.id || row.client_id)}
                    style={{
                      cursor: 'pointer',
                      background: String(selectedId) === String(row.id || row.client_id)
                        ? '#eff6ff'
                        : isDup ? '#fffbeb' : 'transparent',
                      borderBottom: '1px solid #e2e8f0',
                    }}
                  >
                    <td style={{ padding: '8px' }}>{row.sequence}</td>
                    <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{formatTime(row.timestamp || row.logged_at)}</td>
                    <td style={{ padding: '8px' }}>{shortHost(row.host)}</td>
                    <td style={{ padding: '8px' }}>{row.method}</td>
                    <td style={{ padding: '8px', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.path}>{row.path}</td>
                    <td style={{ padding: '8px' }}>{row.status ?? '—'}</td>
                    <td style={{ padding: '8px' }}>{row.durationMs ?? row.duration_ms ?? '—'}</td>
                    <td style={{ padding: '8px' }}>{row.callerTag || row.caller_tag || '—'}</td>
                    <td style={{ padding: '8px', fontSize: '10px' }}>{flags || row.outcome}</td>
                  </tr>
                );
              })}
              {displayEntries.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: '20px', color: 'var(--text-muted)' }}>No log entries match filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="placeholder-card">
          <h3 style={{ marginTop: 0, fontSize: '15px' }}>Entry detail</h3>
          {!selected && <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Select a row to inspect caller stack and nearby trail.</p>}
          {selected && (
            <>
              <dl style={{ fontSize: '12px', lineHeight: 1.6 }}>
                <dt>Outcome</dt><dd>{selected.outcome}</dd>
                <dt>Role / tab</dt><dd>{selected.userRole || selected.user_role || '—'} / {selected.activeTab || selected.active_tab || '—'}</dd>
                <dt>Error</dt><dd>{selected.errorMessage || selected.error_message || '—'}</dd>
                <dt>Query keys</dt><dd>{(selected.queryKeys || selected.query_keys || []).join(', ') || '—'}</dd>
              </dl>
              <pre style={{ fontSize: '10px', background: '#f8fafc', padding: '10px', overflow: 'auto', maxHeight: '160px' }}>
                {(selected.callerStack || selected.caller_stack || '').trim() || 'No stack captured'}
              </pre>
              <h4 style={{ fontSize: '13px', marginBottom: '8px' }}>Nearby trail (±5)</h4>
              <ul style={{ fontSize: '11px', paddingLeft: '16px', margin: 0 }}>
                {contextWindow.map((r) => (
                  <li key={r.id || r.client_id}>
                    {r.sequence} {r.method} {r.path} ({r.outcome || 'network'})
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return iso;
  }
}

function shortHost(host) {
  if (!host) return '—';
  return host.replace('.portahead.com', '').replace('api.ifspd.eu', 'ifspd');
}

function normalizeServerRow(row) {
  return {
    ...row,
    outcome: row.outcome,
    cacheHit: row.cache_hit,
    deduped: row.deduped,
    rateLimited: row.rate_limited,
  };
}